import { signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { FormControl } from '@angular/forms';
import {
  catchError,
  debounceTime,
  defer,
  filter,
  finalize,
  map,
  of,
  Subject,
  switchMap,
  takeUntil,
  type Observable,
} from 'rxjs';
import type { Entry, Module } from '../../../../features/admin/modules/modules.model';
import { UtilityService } from '../../../services/utility.service';
import { debugLog } from '../../../utils/debug-logger';
import { classifyEntriesLoadError } from '../../../utils/entries-load-error';
import type { EntriesLoadError } from '../../../utils/entries-load-error.types';
import type {
  EntryDisplayNameOptions,
  EntrySearchOption,
  LinkageEntriesSearchConfig,
} from './linkage-entries-search.types';

/** The one display-name cascade (selectedInput → titelKeyPath → fallback) for all linkage fields. */
export function entryDisplayName(entry: Entry, module: Module | undefined, options?: EntryDisplayNameOptions): string {
  const fallback = options?.fallback ?? 'Onbekend';
  if (entry.selectedInput) return entry.selectedInput;
  if (module?.titelKeyPath) {
    return UtilityService.keyPathToValue(entry, module.titelKeyPath) ?? fallback;
  }
  return (options?.idFallback ?? true) ? (entry.id ?? fallback) : fallback;
}

type SearchEvent = { kind: 'term'; term: unknown } | { kind: 'focus' };

/** Sentinel meaning "no previous term": always distinct from any control value. */
const NO_TERM: unknown = Symbol('no-term');

/**
 * The one search pipeline behind linkage-items / linkage-tags / linkage-select.
 *
 * Every request path — a typed term, the focus-triggered unfiltered search, and clear() —
 * flows through a single switchMap, so a newer event always cancels the in-flight request.
 * There is no side entrance that can resolve late and overwrite newer results, or fail late
 * and plant a loadError over results that are already showing.
 *
 * `isSearching` is released in a finalize() on the inner request, which also runs when
 * switchMap cancels it — the spinner can never stay on for a request that no longer exists.
 *
 * A failed request clears `searchResults`: the previous term's options must not stay
 * selectable underneath the error alert as if they matched the newer term.
 *
 * Contract for callers that clear (selection made, clear button): call `clear()` — it owns
 * the whole reset: it nulls the connected control without emitting, resets results/error,
 * cancels any in-flight request and forgets the previous term so retyping it searches again.
 * Never clear the control with an emitting setValue: that null would arrive ~debounceMs
 * later as a term event and cancel a focus search started after the clear (the clear button
 * refocuses the field, which fires exactly such a search).
 *
 * Emissions already buffered inside debounceTime when a clear happens (a keystroke or the
 * autocomplete's selected-Entry write less than debounceMs earlier) are dropped by the
 * `term === control.value` guard: after the clear the control no longer holds that value,
 * so the stale emission can neither start a ghost search nor cancel a newer one.
 */
export class LinkageEntriesSearch {
  readonly searchResults = signal<EntrySearchOption[]>([]);
  readonly isSearching = signal(false);

  /** Why the last search request failed, if it did — see entries-load-error.ts. */
  readonly loadError = signal<EntriesLoadError | null>(null);

  readonly #events$ = new Subject<SearchEvent>();
  readonly #config: LinkageEntriesSearchConfig;

  /** Last term delivered to the pipeline; clear() resets it so an identical retype still fires. */
  #lastTerm: unknown = NO_TERM;

  /** The control wired via connect(); clear() writes it and the stale-emission guard reads it. */
  #control: FormControl | undefined;

  constructor(config: LinkageEntriesSearchConfig) {
    this.#config = config;
    this.#events$
      .pipe(
        switchMap(event => this.#handleEvent(event)),
        takeUntil(config.destroy$)
      )
      .subscribe(options => {
        if (options) this.searchResults.set(options);
      });
  }

  /**
   * Routes a control's typed input into the search stream (debounced).
   * A null/empty emission resets results and error. See the class docs for the clear()
   * contract — programmatic clears go through clear(), never through an emitting setValue.
   */
  connect(control: FormControl): void {
    this.#control = control;
    control.valueChanges
      .pipe(
        debounceTime(this.#config.debounceMs ?? 300),
        // Two guards. `term === control.value` drops emissions that were already buffered in
        // debounceTime when a clear rewrote the control — without it a keystroke (or the
        // autocomplete's selected-Entry write) from just before the clear would surface up to
        // debounceMs later, start a ghost search for cleared text and switchMap-cancel a
        // focus search the clear button just started. `term !== #lastTerm` is the dedup whose
        // memory clear() resets: retyping the same term after a clear must fire again, and
        // because the reset is synchronous that holds inside the debounce window too.
        filter(term => term === control.value && term !== this.#lastTerm),
        takeUntil(this.#config.destroy$)
      )
      .subscribe(term => {
        this.#lastTerm = term;
        this.#events$.next({ kind: 'term', term });
      });
  }

  /** Unfiltered search (top results) for focus on an empty field — cancellable like any typed term. */
  searchOnFocus(): void {
    this.#events$.next({ kind: 'focus' });
  }

  /**
   * The one clear path: nulls the connected control (non-emitting), resets results/error,
   * cancels any in-flight request and forgets the previous term. Before connect() has run
   * (the components connect rAF-deferred) there is no control to write, only pipeline state.
   */
  clear(): void {
    this.#lastTerm = NO_TERM;
    this.#control?.setValue(null, { emitEvent: false });
    this.#events$.next({ kind: 'term', term: null });
  }

  #handleEvent(event: SearchEvent): Observable<EntrySearchOption[] | null> {
    if (event.kind === 'focus') {
      return this.#request('');
    }
    const term = event.term;
    if (typeof term === 'string' && term.trim().length > 0) {
      return this.#request(term.trim());
    }
    if (term == null || typeof term === 'string') {
      // Cleared or whitespace-only input: nothing to search for.
      this.searchResults.set([]);
      this.loadError.set(null);
    }
    // A non-string term is the autocomplete's selected Entry object: leave results as-is.
    return of(null);
  }

  /** Null result means "leave searchResults as-is" — the no-op for an unconfigured search module. */
  #request(searchTerm: string): Observable<EntrySearchOption[] | null> {
    const searchMod = this.#config.searchModule();
    if (!searchMod?.id) return of(null);

    // defer so isSearching flips only when switchMap actually subscribes this request —
    // after it has torn down (and finalized) the previous one.
    return defer(() => {
      this.isSearching.set(true);
      this.loadError.set(null);

      return this.#config.entriesService
        .getEntries({
          moduleId: searchMod.id,
          offset: 0,
          limit: 25,
          filter: searchTerm ? `search('${searchTerm}')` : undefined,
        })
        .pipe(
          map(
            entries =>
              entries.map(entry => ({
                label: entryDisplayName(entry, searchMod),
                value: entry,
              })) as EntrySearchOption[]
          ),
          // Inside the inner observable so an error only affects this request, not the
          // outer subscription — a bare catchError outside would kill all future searches.
          catchError((error: HttpErrorResponse) => {
            debugLog(this.#config.namespace, 'search entries failed', {
              searchModule: searchMod.code,
              status: error.status,
            });
            this.loadError.set(classifyEntriesLoadError(error));
            this.searchResults.set([]);
            return of(null);
          }),
          finalize(() => this.isSearching.set(false))
        );
    });
  }
}
